import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const POLL_INTERVAL = 30000;
/** Hvor ofte HTML-en sjekkes på nytt når den ennå ikke peker på ny bundle */
const HTML_RECHECK_INTERVAL = 5 * 60 * 1000;

const ENTRY_SCRIPT = /<script[^>]*type=["']module["'][^>]*src=["']([^"']*\/assets\/[^"']+\.js)["']/i;

const toPath = (src: string) => new URL(src, window.location.href).pathname;

/** Inngangsbundlen siden som kjører nå ble lastet med */
const runningEntry = (): string | null => {
    const src = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]')?.getAttribute('src');
    return src ? toPath(src) : null;
};

/** Inngangsbundlen en reload ville fått — fra HTML-en serveren gir nå */
const servedEntry = async (): Promise<string | null> => {
    const response = await fetch(`/?t=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) return null;
    const match = (await response.text()).match(ENTRY_SCRIPT);
    return match ? toPath(match[1]) : null;
};

/**
 * Banner øverst på siden når en nyere versjon er deployet.
 *
 * version.json deployes sammen med bundlen, mens __APP_BUILD_ID__ er bakt inn
 * i bundlen som kjører. Er de ulike, sitter søkeren trolig på en utdatert
 * versjon der chunk-filene kan være slettet av den nye deployen. Samme
 * mekanisme som lyfti-admin, men et banner i stedet for en toast.
 *
 * version.json alene er ikke nok: på stiftelsenes App Service (FTP-deploy)
 * rendres siden av en index.php utenfor repoet, og den kan peke på en eldre
 * bundle enn den som nettopp ble lagt ut. Da ga «Oppdater» bare den samme
 * gamle bundlen igjen, og banneret kom tilbake i det uendelige. Før banneret
 * vises, sjekkes derfor at HTML-en en reload ville fått faktisk laster en
 * annen bundle enn den som kjører. Bare når version.json har endret seg, så
 * siden (og på SWA Azure-funksjonen bak den) ikke hentes hvert 30. sekund.
 *
 * Bevisst ikke automatisk reload: søkeren kan stå midt i et skjema. Banneret
 * blir stående til søkeren velger Oppdater eller lukker det — da varsles
 * bare en enda nyere versjon igjen.
 */
const NewVersionBanner = () => {
    const { t } = useTranslation();
    const [readyVersion, setReadyVersion] = useState<string | null>(null);
    const [dismissedVersion, setDismissedVersion] = useState<string | null>(null);

    useEffect(() => {
        // version.json finnes bare i produksjonsbygg
        if (!import.meta.env.PROD) return;

        let isMounted = true;
        let lastHtmlCheck: { version: string; at: number } | null = null;

        const isReloadable = async (version: string): Promise<boolean> => {
            const running = runningEntry();
            // Vet vi ikke hva som kjører, stoler vi på version.json som før
            if (!running) return true;

            if (lastHtmlCheck?.version === version && Date.now() - lastHtmlCheck.at < HTML_RECHECK_INTERVAL) {
                return false;
            }
            lastHtmlCheck = { version, at: Date.now() };

            const served = await servedEntry();
            return served !== null && served !== running;
        };

        const check = async () => {
            try {
                const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
                if (!response.ok) return;

                const data = await response.json();
                const version = typeof data?.build === 'string' ? data.build : null;
                if (!version || version === __APP_BUILD_ID__) return;

                if (await isReloadable(version) && isMounted) {
                    setReadyVersion(version);
                }
            } catch {
                // En feilet versjonssjekk skal aldri påvirke appen — nytt forsøk ved neste intervall
            }
        };

        check();
        const interval = setInterval(check, POLL_INTERVAL);

        return () => {
            isMounted = false;
            clearInterval(interval);
        };
    }, []);

    if (!readyVersion || readyVersion === dismissedVersion) {
        return null;
    }

    return (
        <div className="alert alert-info rounded-0 border-0 border-bottom mb-0 py-2 sticky-top no-print" role="status">
            <div className="container d-flex flex-wrap align-items-center justify-content-center gap-2 gap-md-3">
                <span>{t('new_version_available')}</span>
                <div className="d-flex gap-2">
                    <button type="button" className="btn btn-sm btn-primary" onClick={() => window.location.reload()}>
                        {t('new_version_update')}
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setDismissedVersion(readyVersion)}>
                        {t('close')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NewVersionBanner;
